// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0

import { Alert, Card, Drawer, Group, Loader, Paper, Stack, Text, Title } from '@mantine/core';
import {
  IconCircleCheck,
  IconClock,
  IconExclamationCircle,
  IconMapPin,
  IconSettings2,
  IconUser,
} from '@tabler/icons-react';
import { DataTable } from 'mantine-datatable';
import type { JSX } from 'react';
import { useCoverageEligibilityDetail } from '../../hooks/useCoverageEligibilityDetail';

export interface EligibilityDetailDrawerProps {
  cerRef: string | null;
  opened: boolean;
  onClose: () => void;
}

function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

export function EligibilityDetailDrawer({ cerRef, opened, onClose }: EligibilityDetailDrawerProps): JSX.Element {
  const { detail, loading, error } = useCoverageEligibilityDetail(cerRef);

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size="xl"
      title={
        <Group justify="space-between" w="100%" pr="md">
          <Text fw={600}>Eligibility Check Details:</Text>
          {detail && (
            <Group gap="xs">
              <IconClock size={14} />
              <Text size="sm">{formatDateTime(detail.dateChecked)}</Text>
            </Group>
          )}
        </Group>
      }
    >
      {loading && (
        <Stack align="center" pt="xl">
          <Loader />
        </Stack>
      )}

      {!loading && error && (
        <Alert color="red" icon={<IconExclamationCircle />} title="Error loading details">
          {error}
        </Alert>
      )}

      {!loading && detail && (
        <Stack gap="md">
          <Alert
            color={detail.isActive ? 'green' : 'red'}
            icon={<IconCircleCheck />}
            title={detail.isActive ? 'Active Coverage' : 'Inactive Coverage'}
          >
            {detail.isActive
              ? 'A service with active coverage was found for this patient.'
              : 'No active coverage was found for this patient.'}
          </Alert>

          <Group grow gap="sm" align="stretch">
            <Card withBorder padding="sm">
              <Group gap="xs" c="dimmed" mb={4}>
                <IconUser size={14} />
                <Text size="sm">Subscriber</Text>
              </Group>
              <Text fw={700} size="lg">
                {detail.subscriber.name}
              </Text>
              <Text size="sm" c="dimmed">
                MRN: {detail.subscriber.mrn}
              </Text>
            </Card>

            <Card withBorder padding="sm">
              <Group gap="xs" c="dimmed" mb={4}>
                <IconSettings2 size={14} />
                <Text size="sm">Payor</Text>
              </Group>
              <Text fw={700} size="lg">
                {detail.payor.name}
              </Text>
              <Text size="sm" c="dimmed">
                ID: {detail.payor.id}
              </Text>
            </Card>

            <Card withBorder padding="sm">
              <Group gap="xs" c="dimmed" mb={4}>
                <IconMapPin size={14} />
                <Text size="sm">Provider</Text>
              </Group>
              <Text fw={700} size="lg">
                {detail.provider.name}
              </Text>
              <Text size="sm" c="dimmed">
                NPI: {detail.provider.npi}
              </Text>
            </Card>
          </Group>

          <Title order={2}>Benefits</Title>
          <Text>{detail.stcLabel}</Text>

          <Paper withBorder p="sm">
            <Group justify="space-between">
              <Text fw={600}>Estimated Patient Responsibility:</Text>
              <Text fw={700}>{detail.estimatedResponsibility}</Text>
            </Group>
          </Paper>

          {detail.benefitGroups.map((group) => (
            <Stack key={group.label} gap="xs">
              <Text fw={600}>{group.label}</Text>
              <Paper withBorder>
                <DataTable
                  records={group.rows}
                  idAccessor="id"
                  columns={[
                    { accessor: 'type', title: 'Type' },
                    { accessor: 'network', title: 'Network' },
                    { accessor: 'coverage', title: 'Coverage' },
                    { accessor: 'terms', title: 'Terms' },
                  ]}
                  striped
                  fz="sm"
                />
              </Paper>
            </Stack>
          ))}
        </Stack>
      )}
    </Drawer>
  );
}
